import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  BookOpen, X, ArrowRight, Star, 
  Users, Trophy, School, CheckCircle2,
  Facebook, Instagram, Phone, Mail, MapPin, 
  ChevronLeft, ChevronDown, Quote, ArrowUpRight, 
  MessageCircle, Award, Target, Building2, HelpCircle, Image as ImageIcon
} from 'lucide-react';

// =============================================
// DESIGN 1 — MODERN ELITE
// Backbone: 100% identical to Design0_Features
// UI: Distinct serif/dark aesthetic
// =============================================

const Design1_ModernElite: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [activeTab, setActiveTab] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedView, setExpandedView] = useState<'staff' | 'board' | 'gallery' | 'facilities' | 'anthems' | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── INJECT CUSTOM FONT from Firebase theme ────────────────────────────────
  useEffect(() => {
    const _p: any = previewData || {};
    const headingFont = _p.theme?.fonts?.heading || _p.fonts?.heading || null;
    const bodyFont    = _p.theme?.fonts?.body    || _p.fonts?.body    || null;
    const fonts = [headingFont, bodyFont].filter(Boolean);
    if (fonts.length > 0) {
      const linkId = 'design1-google-fonts';
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.id   = linkId;
        link.rel  = 'stylesheet';
        document.head.appendChild(link);
      }
      const familyParam = fonts.map((f: string) => `family=${f.replace(/ /g, '+')}:wght@400;600;700;900`).join('&');
      link.href = `https://fonts.googleapis.com/css2?${familyParam}&display=swap`;
    }
  }, [previewData]);

  const safeUrl = (file: any) => {
    if (!file) return undefined;
    if (typeof file === 'string') return file;
    try { return URL.createObjectURL(file); } catch (e) { return undefined; }
  };

  const normalizeMediaField = (value: any) => {
    if (!value) return null;
    return typeof value === 'string' ? value : safeUrl(value);
  };

  // ── DATA MAPPING: supports both Firebase nested doc format and flat preview form state ─────────
  // Firebase stores: mission.text, principal.name, stats.totalStudents, headerPhotoUrls[], etc.
  // Preview form stores: mission (string), principalName, totalStudents, headerPhotos[], etc.
  const _p: any = previewData || {};

  // Header photos: Firebase uses headerPhotoUrls[], form uses headerPhotos[]
  const _rawHeaderPhotos = (_p.headerPhotoUrls && _p.headerPhotoUrls.filter((x: any) => x).length > 0)
    ? _p.headerPhotoUrls
    : (_p.headerPhotos && _p.headerPhotos.filter((x: any) => x !== null).length > 0)
      ? _p.headerPhotos
      : null;

  // Stats: Firebase uses stats.{} object, form uses flat fields
  const _stats: any = _p.stats || {};

  // Principal: Firebase uses principal.{} object, form uses flat fields
  const _principal: any = _p.principal || {};

  // Story sections: Firebase uses mission.text / mission.imageUrl, form uses plain strings
  const _mission = typeof _p.mission === 'object' ? (_p.mission?.text || '') : (_p.mission || '');
  const _vision = typeof _p.vision === 'object' ? (_p.vision?.text || '') : (_p.vision || '');
  const _about = typeof _p.about === 'object' ? (_p.about?.text || '') : (_p.about || '');
  const _nationalAnthem = typeof _p.nationalAnthem === 'object' ? (_p.nationalAnthem?.text || '') : (_p.nationalAnthem || '');
  const _schoolAnthem = typeof _p.schoolAnthem === 'object' ? (_p.schoolAnthem?.text || '') : (_p.schoolAnthem || '');
  const _whyChooseUs = typeof _p.whyChooseUs === 'object' ? (_p.whyChooseUs?.text || '') : (_p.whyChooseUs || '');

  // Story images: Firebase uses mission.imageUrl, form uses missionFile (File)
  const _missionPhoto = (typeof _p.mission === 'object' ? _p.mission?.imageUrl : null)
    || (_p.missionPhoto ? safeUrl(_p.missionPhoto) : null);
  const _visionPhoto = (typeof _p.vision === 'object' ? _p.vision?.imageUrl : null)
    || (_p.visionPhoto ? safeUrl(_p.visionPhoto) : null);
  const _aboutPhoto = (typeof _p.about === 'object' ? _p.about?.imageUrl : null)
    || (_p.aboutPhoto ? safeUrl(_p.aboutPhoto) : null);
  const _whyChooseUsPhoto = (typeof _p.whyChooseUs === 'object' ? _p.whyChooseUs?.imageUrl : null)
    || (_p.whyChooseUsPhoto ? safeUrl(_p.whyChooseUsPhoto) : null);

  // Curriculum: Firebase uses curriculum.{} object, form uses flat fields
  const _curriculum: any = _p.curriculum || {};

  // Contact: Firebase uses contact.{} object, form uses flat fields
  const _contact: any = _p.contact || {};

  // Admissions: Firebase uses admissions.{} object
  const _admissions: any = _p.admissions || {};

  // Fees: Firebase uses fees.{} object
  const _fees: any = _p.fees || {};

  // Logo: Firebase uses logoUrl string, form uses logo File
  const _logo = _p.logoUrl ? _p.logoUrl : (_p.logo ? safeUrl(_p.logo) : null);

  const data = {
    name: _p.name || "St. Augustine's National Academy",
    motto: _p.motto || "In Veritate et Virtute (In Truth and Virtue)",
    logo: _logo || "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=256&auto=format&fit=crop",
    headerPhotos: _rawHeaderPhotos || [
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=1200&auto=format&fit=crop"
    ],
    stats: {
      students: _stats.totalStudents || _p.totalStudents || "1,450",
      staff: _stats.totalStaff || _p.totalStaff || "92",
      founded: _stats.yearFounded || _p.yearFounded || "1974",
      alumni: _stats.totalAlumni || _p.totalAlumni || "8,000+"
    },
    principal: {
      name: _principal.name || _p.principalName || "Dr. Richard M. Mutua",
      message: _principal.message || _p.principalMessage || "Welcome to St. Augustine's, a leading institution where we harmonize academic discipline with character development to mold the leaders of tomorrow.",
      photo: _principal.photoUrl || (_p.principalPhoto ? safeUrl(_p.principalPhoto) : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop")
    },
    mission: _mission || "To provide a holistic and inclusive learning environment that fosters academic brilliance, integrity, and global citizenship.",
    vision: _vision || "To be the premier national center of excellence, recognized globally for academic innovation and transformative leadership.",
    about: _about || "A distinguished national institution dedicated to the pursuit of excellence in all spheres of life.",
    history: _p.history || _p.historyText || "Established in 1974 as a community initiative, our school was later elevated to National status in 2011, continuing a heritage of producing top-tier professionals and leaders for the continent.",
    aboutPhoto: _aboutPhoto || "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070",
    missionPhoto: _missionPhoto || "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070",
    visionPhoto: _visionPhoto || "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=2070",
    whyChooseUsPhoto: _whyChooseUsPhoto || "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2070",
    nationalAnthem: _nationalAnthem || "O God of all creation\nBless this our land and nation\nJustice be our shield and defender\nMay we dwell in unity\nPeace and liberty\nPlenty be found within our borders.\n\nLet one and all arise\nWith hearts both strong and true\nService be our earnest endeavour\nAnd our homeland of Kenya\nHeritage of splendour\nFirm may we stand to defend.\n\nLet all with one accord\nIn common bond united\nBuild this our nation together\nAnd the glory of Kenya\nThe fruit of our labour\nFill every heart with thanksgiving.",
    schoolAnthem: _schoolAnthem || "Hail to our alma mater, standing tall and true,\nGuiding lights of knowledge, red and brilliant blue.\nIn these halls of wisdom, we learn to lead and serve,\nBuilding strong foundations, with unyielding nerve.\n\nThrough the trials and triumphs, together we will rise,\nReaching for the future, beneath the open skies.\nHonor, truth, and courage, forever be our guide,\nIn our school's great legacy, we take eternal pride!",
    whyUs: _whyChooseUs || "We combine a tradition of excellence with state-of-the-art facilities and a passionate faculty dedicated to cross-disciplinary learning.",
    coreValues: _p.coreValues || ["Integrity", "Excellence", "Innovation", "Inclusivity", "Service"],
    quote: {
      text: (typeof _p.quote === 'object' ? _p.quote?.text : _p.quote) || "The beautiful thing about learning is that no one can take it away from you.",
      author: (typeof _p.quote === 'object' ? _p.quote?.author : _p.quoteAuthor) || "B.B. King"
    },
    curriculum: {
      description: _curriculum.description || _p.curriculumDesc || "Our comprehensive curriculum follows the National Competency Based framework, enhanced with international STEM integration for a global perspective.",
      compulsory: _curriculum.compulsorySubjects || _p.compulsorySubjects || ["English", "Mathematics", "Kiswahili", "Chemistry", "Biology", "Physics"],
      optional: _curriculum.optionalSubjects || _p.optionalSubjects || ["Computer Studies", "French", "Business Studies", "Agriculture", "Music", "Aviation"],
      photo: _curriculum.photo ? _curriculum.photo : (_p.curriculumPhoto ? safeUrl(_p.curriculumPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070")
    },
    activities: (_p.activities && _p.activities.length > 0) ? _p.activities.map((a: any) => ({
      ...a,
      photo: a.photoUrl || normalizeMediaField(a.photo)
    })) : [
      { id: "1", title: "Robotics Club", description: "Design and program modern robots with Python.", photo: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop" },
      { id: "2", title: "Drama Society", description: "Annual theatrical productions and national competitions.", photo: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?q=80&w=400&auto=format&fit=crop" },
      { id: "3", title: "Basketball Team", description: "Our varsity team 'The Eagles' are the regional champions.", photo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=400&auto=format&fit=crop" }
    ],
    facilities: (_p.facilities && _p.facilities.length > 0) ? _p.facilities.map((f: any) => ({
      ...f,
      photoFile: f.photoUrl || normalizeMediaField(f.photoFile)
    })) : [
      { id: "1", categoryId: "Technology", title: "Modern Science Lab", description: "Fully equipped biology and chemistry laboratories.", photoFile: "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=400&auto=format&fit=crop" },
      { id: "2", categoryId: "Academic", title: "Digital Library", description: "Access to over 10,000 e-books and research journals.", photoFile: "https://images.unsplash.com/photo-1521587760476-6c12a7b041da?q=80&w=400&auto=format&fit=crop" },
      { id: "3", categoryId: "Sports", title: "Indoor Sports Arena", description: "Olympic-sized pool and multi-purpose gymnasium.", photoFile: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=400&auto=format&fit=crop" },
      { id: "4", categoryId: "Arts", title: "Creative Arts Center", description: "Soundproof music studios and fine arts workshops.", photoFile: "https://images.unsplash.com/photo-1460518451285-8f16c4f0340b?q=80&w=400&auto=format&fit=crop" },
      { id: "5", categoryId: "Medical", title: "Student Health Clinic", description: "24/7 medical services with trained resident nurses.", photoFile: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=400&auto=format&fit=crop" },
      { id: "6", categoryId: "Residential", title: "Premium Boarding Houses", description: "Comfortable, modern living spaces with high-speed internet.", photoFile: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=400&auto=format&fit=crop" }
    ],
    // Staff: Firebase uses staffMembers[].photoUrl, form uses staffMembers[].photoFile (File)
    staff: (_p.staffMembers && _p.staffMembers.length > 0) ? _p.staffMembers.map((s: any) => ({
      ...s,
      // Normalise photo: Firebase stores photoUrl (string), form stores photoFile (File)
      photoFile: s.photoUrl || (s.photoFile && typeof s.photoFile === 'string' ? s.photoFile : (s.photoFile ? safeUrl(s.photoFile) : null)),
      bio: s.description || s.bio || 'Faculty'
    })) : [
      { id: "1", title: "Mr.", name: "Peter Otieno", bio: "HOD Sciences", photoFile: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Ms.", name: "Sarah Wanjiku", bio: "HOD Humanities", photoFile: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Dr.", name: "David Kimani", bio: "HOD Languages", photoFile: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Mrs.", name: "Mary Atieno", bio: "HOD Mathematics", photoFile: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Mr.", name: "John Kamau", bio: "Sports Director", photoFile: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Ms.", name: "Grace Njeri", bio: "Counselor", photoFile: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=256&auto=format&fit=crop" }
    ],
    // Board: Firebase uses boardMembers[].photoUrl, form uses boardMembers[].photoFile (File)
    board: (_p.boardMembers && _p.boardMembers.length > 0) ? _p.boardMembers.map((b: any) => ({
      ...b,
      photoFile: b.photoUrl || (b.photoFile && typeof b.photoFile === 'string' ? b.photoFile : (b.photoFile ? safeUrl(b.photoFile) : null))
    })) : [
      { id: "1", title: "Rev.", name: "John Mark", role: "Chairman BOM", photoFile: "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Dr.", name: "Alice Mwangi", role: "Treasurer", photoFile: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Hon.", name: "Samuel Korir", role: "Community Rep", photoFile: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Eng.", name: "Francis Njuguna", role: "Infrastructure", photoFile: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Prof.", name: "Jane Kemuntu", role: "Advisor", photoFile: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Lady Justice", name: "Ruth Kerubo", role: "Legal", photoFile: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop" }
    ],
    // Gallery: Firebase uses gallery[].photoUrl, form uses galleryItems[].photoFile (File)
    gallery: ((_p.gallery && _p.gallery.length > 0) ? _p.gallery : (_p.galleryItems && _p.galleryItems.length > 0) ? _p.galleryItems : null)?.map((g: any) => ({
      ...g,
      photoFile: g.photoUrl || (g.photoFile && typeof g.photoFile === 'string' ? g.photoFile : (g.photoFile ? safeUrl(g.photoFile) : null))
    })) || [
      { id: "1", categoryId: "Campus", name: "Main Wing", description: "Our historic main administration building.", photoFile: "https://images.unsplash.com/photo-1525921429624-479b6a29d840?q=80&w=600&auto=format&fit=crop" },
      { id: "2", categoryId: "Events", name: "Sports Day", description: "Annual regional championships.", photoFile: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=600&auto=format&fit=crop" },
      { id: "3", categoryId: "Infrastructure", name: "Library", description: "State-of-the-art research center.", photoFile: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=600&auto=format&fit=crop" },
      { id: "4", categoryId: "Students", name: "Tech Club", description: "Students building a robotics prototype.", photoFile: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=600&auto=format&fit=crop" },
      { id: "5", categoryId: "Trips", name: "Mountain Hike", description: "Biology class exploring Mount Kenya flora.", photoFile: "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=600&auto=format&fit=crop" }
    ],
    // Contact: Firebase uses contact.{} object, form uses flat fields
    contact: {
      phones: _contact.phones || _p.phones || ["+254 712 345 678", "+254 789 012 345"],
      email: _contact.email || _p.email || "info@staugustines.ac.ke",
      whatsapp: _contact.whatsapp || _p.whatsapp || "254712345678",
      facebook: (_contact.social?.facebook) || _contact.facebook || _p.fbLink || "",
      twitter: (_contact.social?.twitter) || _contact.twitter || _p.twitterLink || "",
      instagram: (_contact.social?.instagram) || _contact.instagram || _p.instaLink || "",
      address: _contact.address || _p.address || "Main Campus, Kisumu-Kakamega Road, Kisumu",
      mapsLink: _contact.mapsLink || _p.mapsLink || "https://www.google.com/maps/embed?pb=..."
    },
    // Admissions: Firebase uses admissions.{} object, form uses flat fields
    admissions: {
      process: _admissions.process || _p.admissionProcess || "Our intake happens annually in January. Prospective students must submit their applications through our online portal or in-person at the Registrar's Office.",
      requirements: _admissions.requirements || _p.admissionRequirements || ["Certified Birth Certificate", "KCPE Result Slip", "Leaving Certificate", "Medical Examination Report"],
      formFile: _admissions.formUrl || _p.admissionFormFile || null
    },
    // Payments: Firebase uses fees.paymentMethods, form uses paymentMethods
    payments: (_fees.paymentMethods && _fees.paymentMethods.length > 0)
      ? _fees.paymentMethods
      : (_p.paymentMethods && _p.paymentMethods.length > 0)
        ? _p.paymentMethods
        : [
          { id: "1", method: "M-Pesa Paybill", details: "Business No: 123456 | Account: Student Name" },
          { id: "2", method: "KCB Bank", details: "Acc Name: St. Augustine's | Acc No: 0987654321" }
        ],
    // Fees: Firebase uses fees.breakdown, form uses feeBreakdown
    feePolicy: _fees.breakdown || _p.feeBreakdown || "School fees are due on or before the first day of each term. Payment plans are available upon request through the Finance Office."
  };

  // Slideshow
  useEffect(() => {
    if (data.headerPhotos.length <= 1) return;
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % data.headerPhotos.length), 5000);
    return () => clearInterval(interval);
  }, [data.headerPhotos.length]);

  const filteredGallery = data.gallery.filter((item: any) => activeTab === 'All' || (item.categoryId || item.category) === activeTab);

  // ── CSS VARIABLES: wire form colors → published website ──────────────────
  // Handles all three save formats:
  //   1. theme.colors.{primary,secondary,accent}  (design selector / theme picker)
  //   2. colors.{primary,secondary,accent}         (flat colors object)
  //   3. primaryColor / secondaryColor / accentColor (legacy flat fields from form)
  const _pRaw: any = previewData || {};
  const _themeColors = _pRaw.theme?.colors || _pRaw.colors || {};
  const _themeFonts  = _pRaw.theme?.fonts  || _pRaw.fonts  || {};
  const cssVars: React.CSSProperties & Record<string, string> = {
    '--color-primary':    _themeColors.primary     || _pRaw.primaryColor    || '#1e3a5f',
    '--color-secondary':  _themeColors.secondary   || _pRaw.secondaryColor  || '#228b22',
    '--color-accent':     _themeColors.accent      || _pRaw.accentColor     || '#ffd700',
    '--color-background': _themeColors.background  || _pRaw.backgroundColor || '#ffffff',
    '--color-text':       _themeColors.text        || _pRaw.textColor       || '#1e293b',
    '--color-text-muted': _themeColors.textMuted   || _pRaw.mutedColor      || '#64748b',
    '--font-heading': _themeFonts.heading
      ? `'${_themeFonts.heading}', serif`
      : "'Playfair Display', Georgia, serif",
    '--font-body': _themeFonts.body
      ? `'${_themeFonts.body}', sans-serif`
      : "'Inter', system-ui, sans-serif",
  };

  return (
    <div
      ref={containerRef}
      style={cssVars as React.CSSProperties}
      className="bg-white text-slate-900 leading-relaxed selection:bg-[var(--color-secondary)] selection:text-white overflow-x-hidden"
    >

      {/* ── NAV ── */}
      <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-slate-100 py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer group">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden border transition-all ${isScrolled ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/15 border-white/20 backdrop-blur-sm'}`}>
              {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="h-full w-full object-contain p-1" /> : <School size={22} className={isScrolled ? 'text-[var(--color-primary)]' : 'text-white'} />}
            </div>
            <div>
              <h2 className={`text-base font-bold font-serif tracking-tight leading-none transition-colors ${isScrolled ? 'text-slate-900' : 'text-white'}`}>{data.name}</h2>
              <p className={`text-[9px] font-semibold uppercase tracking-widest mt-0.5 transition-colors ${isScrolled ? 'text-slate-500' : 'text-white/60'}`}>{data.motto}</p>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-8">
            {[{ name: 'Home', href: '#' }, { name: 'About', href: '#about' }, { name: 'Academics', href: '#academics' }, { name: 'Gallery', href: '#gallery' }, { name: 'Contact', href: '#contact' }].map((item) => (
              <a key={item.name} href={item.href} className={`text-xs font-semibold uppercase tracking-widest transition-colors ${isScrolled ? 'text-slate-600 hover:text-[var(--color-primary)]' : 'text-white/80 hover:text-white'}`}>
                {item.name}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-end justify-start overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0">
          {data.headerPhotos.map((p: any, i: number) => (
            <img key={i} src={safeUrl(p)} alt={`Hero ${i}`} className={`absolute inset-0 w-full h-full object-cover transition-all duration-[2000ms] ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-24 pt-40">
          <div className="max-w-2xl space-y-6">
            <p className="text-[var(--color-accent)] text-xs font-bold uppercase tracking-[0.3em]">Welcome to Excellence</p>
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white leading-tight drop-shadow-xl">
              {data.name}
            </h1>
            <p className="text-lg text-slate-200 font-light italic leading-relaxed border-l-2 border-[var(--color-secondary)] pl-5">
              "{data.motto}"
            </p>
            <div className="flex gap-4 pt-4">
              <a href="#about" className="px-6 py-3 bg-[var(--color-primary)] text-white text-xs font-bold uppercase tracking-widest rounded-full hover:bg-[var(--color-secondary)] transition-colors">
                Learn More
              </a>
              <a href="#contact" className="px-6 py-3 border border-white/30 text-white text-xs font-bold uppercase tracking-widest rounded-full hover:bg-white/10 transition-colors backdrop-blur-sm">
                Contact Us
              </a>
            </div>
          </div>
        </div>
        {/* Slide indicators */}
        <div className="absolute bottom-8 right-8 z-10 flex gap-2">
          {data.headerPhotos.map((_: any, i: number) => (
            <button key={i} onClick={() => setCurrentSlide(i)} title={`Go to slide ${i + 1}`} aria-label={`Go to slide ${i + 1}`} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-white w-6' : 'bg-white/40'}`} />
          ))}
        </div>
      </section>

      {/* ── ABOUT — History ── */}
      <section id="about" className="py-24 bg-white scroll-mt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 space-y-28">

          {/* History block */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Our Narrative</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)] tracking-tight">School History</h2>
                <div className="w-14 h-1 bg-[var(--color-accent)]" />
              </div>
              <p className="text-slate-600 leading-relaxed text-base">{data.history}</p>
              <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-100">
                <div><p className="text-3xl font-serif font-bold text-[var(--color-primary)]">{data.stats.students}</p><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-1">Students</p></div>
                <div><p className="text-3xl font-serif font-bold text-[var(--color-primary)]">{data.stats.staff}</p><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-1">Staff Members</p></div>
                <div><p className="text-3xl font-serif font-bold text-[var(--color-primary)]">{data.stats.founded}</p><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-1">Founded</p></div>
                <div><p className="text-3xl font-serif font-bold text-[var(--color-primary)]">{data.stats.alumni}</p><p className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mt-1">Alumni Network</p></div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl border-8 border-slate-50 relative z-10 group">
                <img src={data.aboutPhoto} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="School History" />
              </div>
              <div className="absolute -bottom-8 -left-8 w-56 h-56 bg-[var(--color-primary)]/5 z-0 rounded-3xl border border-[var(--color-primary)]/10 rotate-12" />
            </div>
          </div>

          {/* Vision & Mission */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="grid grid-cols-2 gap-4 relative z-10">
                <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-lg mt-10 border-4 border-white group">
                  <img src={data.visionPhoto} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Vision" />
                </div>
                <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-lg border-4 border-white group">
                  <img src={data.missionPhoto} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Mission" />
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[80%] bg-slate-50 z-0 rounded-3xl -rotate-6" />
            </div>
            <div className="order-1 lg:order-2 space-y-10">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Core Principles</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)] tracking-tight">Vision & Mission</h2>
                <div className="w-14 h-1 bg-[var(--color-accent)]" />
              </div>
              <div className="space-y-8">
                <div className="space-y-3 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="w-10 h-10 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-xl flex items-center justify-center"><Award size={20} /></div>
                  <h4 className="font-bold text-lg font-serif text-slate-900">Our Vision</h4>
                  <p className="text-slate-600 leading-relaxed text-base">{data.vision}</p>
                </div>
                <div className="space-y-3 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="w-10 h-10 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-xl flex items-center justify-center"><Target size={20} /></div>
                  <h4 className="font-bold text-lg font-serif text-slate-900">Our Mission</h4>
                  <p className="text-slate-600 leading-relaxed text-base">{data.mission}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Us & Anthems */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-10">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs flex items-center gap-2"><HelpCircle size={12} /> Excellence</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)] tracking-tight">Why Choose Us?</h2>
                <div className="w-14 h-1 bg-[var(--color-accent)]" />
              </div>
              <p className="text-slate-600 leading-relaxed text-base">{data.whyUs}</p>

              {/* Anthems panel */}
              <div className="p-8 bg-[var(--color-primary)] text-white rounded-3xl space-y-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />
                <h4 className="font-bold uppercase text-xs tracking-widest text-[var(--color-accent)]">Institutional Anthems</h4>
                <div className="grid sm:grid-cols-2 gap-4 relative z-10">
                  <div onClick={() => setExpandedView('anthems')} className="p-5 bg-white/5 rounded-2xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-semibold text-slate-200 uppercase tracking-wide">National Anthem</p>
                      <ArrowUpRight size={14} className="text-[var(--color-accent)]" />
                    </div>
                    <p className="text-xs italic text-slate-400 line-clamp-2">{data.nationalAnthem}</p>
                  </div>
                  <div onClick={() => setExpandedView('anthems')} className="p-5 bg-white/5 rounded-2xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-semibold text-slate-200 uppercase tracking-wide">School Anthem</p>
                      <ArrowUpRight size={14} className="text-[var(--color-accent)]" />
                    </div>
                    <p className="text-xs italic text-slate-400 line-clamp-2">{data.schoolAnthem}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-xl border-8 border-white relative z-10 group">
                <img src={data.whyChooseUsPhoto} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Excellence" />
              </div>
              <div className="absolute top-4 right-4 w-full h-full bg-[var(--color-secondary)]/10 z-0 rounded-3xl -rotate-3" />
            </div>
          </div>
        </div>
      </section>

      {/* ── ACADEMICS ── */}
      <section id="academics" className="py-24 bg-slate-50 scroll-mt-20 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl border-8 border-white relative z-10 group">
                <img src={data.curriculum.photo} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Academics" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-full h-full bg-[var(--color-primary)]/5 z-0 rounded-3xl rotate-3" />
            </div>
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Academic Program</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)] tracking-tight">Our Curriculum</h2>
                <p className="text-slate-600 leading-relaxed text-base">{data.curriculum.description}</p>
                <div className="w-14 h-1 bg-[var(--color-accent)]" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-6 bg-white border border-slate-100 rounded-2xl shadow-sm space-y-4">
                  <h4 className="font-bold text-slate-900 border-b pb-3 flex items-center gap-2 text-sm"><BookOpen size={16} className="text-[var(--color-primary)]" /> Core Subjects</h4>
                  <div className="flex flex-wrap gap-2">
                    {data.curriculum.compulsory.map((s: string, i: number) => <span key={i} className="px-3 py-1.5 bg-slate-50 rounded-full text-xs font-medium text-slate-600 border border-slate-200">{s}</span>)}
                  </div>
                </div>
                <div className="p-6 bg-[var(--color-primary)] text-white rounded-2xl shadow-sm space-y-4">
                  <h4 className="font-bold border-b border-white/20 pb-3 flex items-center gap-2 text-sm"><ArrowRight size={16} className="text-[var(--color-accent)]" /> Electives</h4>
                  <div className="flex flex-wrap gap-2">
                    {data.curriculum.optional.map((s: string, i: number) => <span key={i} className="px-3 py-1.5 bg-white/10 rounded-full text-xs font-medium text-white border border-white/20">{s}</span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLUBS & ACTIVITIES ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14 space-y-3">
            <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Holistic Development</span>
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)]">Clubs & Activities</h2>
            <div className="w-14 h-1 bg-[var(--color-accent)] mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.activities.length > 0 ? data.activities.map((a: any, i: number) => (
              <div key={i} className="group border border-slate-100 rounded-2xl overflow-hidden hover:shadow-xl transition-all bg-white">
                <div className="h-48 overflow-hidden bg-slate-100 relative">
                  {a.photo ? <img src={safeUrl(a.photo)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="Activity" /> : <div className="w-full h-full flex items-center justify-center"><Trophy className="text-slate-300" size={40} /></div>}
                </div>
                <div className="p-6">
                  <h4 className="font-bold text-lg font-serif text-slate-900 mb-2">{a.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{a.description}</p>
                </div>
              </div>
            )) : (
              <div className="lg:col-span-3 text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                <Trophy size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">No Activities Added Yet</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── FACILITIES ── */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-14">
            <div className="space-y-3">
              <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">School Infrastructure</span>
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)]">Modern Facilities</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.facilities.length > 0 ? data.facilities.slice(0, 4).map((f: any, i: number) => (
              <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 hover:border-[var(--color-secondary)] transition-all group shadow-sm">
                <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-slate-100">
                  {f.photoFile ? <img src={safeUrl(f.photoFile)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="Facility" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="text-slate-300" /></div>}
                </div>
                <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-widest mb-1 italic">{f.categoryId}</p>
                <h4 className="font-bold font-serif text-slate-900 mb-1">{f.title}</h4>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{f.description}</p>
              </div>
            )) : (
              <div className="lg:col-span-4 text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100 italic text-slate-400">
                School development details will appear here.
              </div>
            )}
          </div>
          {data.facilities.length > 4 && (
            <div className="mt-10">
              <button onClick={() => setExpandedView('facilities')} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center justify-center gap-2">
                View More <ArrowUpRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── PRINCIPAL ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2 relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl bg-slate-100 border-8 border-slate-50">
                {data.principal.photo ? <img src={safeUrl(data.principal.photo)} className="w-full h-full object-cover" alt="Principal" /> : <div className="w-full h-full flex items-center justify-center"><Users size={60} className="text-slate-300" /></div>}
              </div>
              <div className="absolute -bottom-5 -right-5 bg-[var(--color-primary)] text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <p className="text-sm font-bold uppercase tracking-wide mb-0.5">{data.principal.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/70">Chief Principal</p>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-7">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Head of Institution</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)] tracking-tight">Principal's Address</h2>
                <div className="w-14 h-1 bg-[var(--color-accent)]" />
              </div>
              <Quote className="text-[var(--color-primary)]/20" size={60} />
              <p className="text-xl text-slate-600 leading-relaxed italic font-light">"{data.principal.message}"</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAFF & BOARD ── */}
      <section id="staff" className="py-24 bg-slate-50 border-y border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20">

            {/* Faculty */}
            <div className="space-y-10">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--color-secondary)] border-l-4 border-[var(--color-secondary)] pl-4">Academic Faculty</h4>
              <div className="grid grid-cols-2 gap-5">
                {data.staff.slice(0, 4).map((s: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 group bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover:border-[var(--color-secondary)] transition-all">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-200 shrink-0 border-2 border-white shadow-sm flex items-center justify-center">
                      {s.photoFile ? <img src={safeUrl(s.photoFile)} alt={s.name} className="w-full h-full object-cover" /> : <Users size={20} className="text-slate-400" />}
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-900 text-sm leading-none mb-1">{s.title} {s.name}</h5>
                      <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-wide">{s.bio || "Staff"}</p>
                    </div>
                  </div>
                ))}
              </div>
              {data.staff.length > 4 && (
                <button onClick={() => setExpandedView('staff')} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center justify-center gap-2">
                  View More <ArrowUpRight size={14} />
                </button>
              )}
            </div>

            {/* Board */}
            <div className="space-y-10">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--color-secondary)] border-l-4 border-[var(--color-secondary)] pl-4">Board of Management</h4>
              <div className="grid grid-cols-2 gap-5">
                {data.board.slice(0, 4).map((b: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 group bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover:border-[var(--color-secondary)] transition-all">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-200 shrink-0 border-2 border-white shadow-sm flex items-center justify-center">
                      {b.photoFile ? <img src={safeUrl(b.photoFile)} alt={b.name} className="w-full h-full object-cover" /> : <School size={20} className="text-slate-400" />}
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-900 text-sm leading-none mb-1">{b.title} {b.name}</h5>
                      <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-wide">{b.role}</p>
                    </div>
                  </div>
                ))}
              </div>
              {data.board.length > 4 && (
                <button onClick={() => setExpandedView('board')} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center justify-center gap-2">
                  View More <ArrowUpRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section id="gallery" className="py-24 bg-white scroll-mt-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-3">
              <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Visual Archive</span>
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)]">Student Life</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                <button key={cat} onClick={() => setActiveTab(cat)} className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all border ${activeTab === cat ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)] shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)]'}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredGallery.slice(0, 4).map((item: any, i: number) => (
            <div key={i} onClick={() => setLightboxIndex(i)} className="cursor-pointer relative overflow-hidden rounded-2xl group shadow-sm aspect-square bg-slate-200">
              {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                <p className="text-[9px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-1">{item.categoryId || item.category}</p>
                {item.name && <h4 className="text-white font-bold text-base leading-tight">{item.name}</h4>}
                {item.description && <p className="text-slate-300 text-xs leading-relaxed line-clamp-2 mt-1">{item.description}</p>}
              </div>
            </div>
          ))}
        </div>
        {filteredGallery.length > 4 && (
          <div className="max-w-7xl mx-auto px-6 mt-10">
            <button onClick={() => setExpandedView('gallery')} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center justify-center gap-2">
              View More <ArrowUpRight size={16} />
            </button>
          </div>
        )}
      </section>

      {/* ── ADMISSIONS & FEES ── */}
      <section id="admissions" className="py-24 bg-slate-950 text-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="space-y-10">
              <div className="space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight">Admissions</h2>
                <p className="text-[var(--color-accent)] font-bold uppercase tracking-widest text-[10px]">Enrollment Process</p>
              </div>
              <div className="space-y-7">
                <p className="text-slate-400 leading-relaxed text-base italic border-l-4 border-[var(--color-secondary)] pl-6">{data.admissions.process}</p>
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500">General Requirements</h4>
                  <ul className="space-y-3">
                    {data.admissions.requirements.map((req: string, i: number) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                        <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0" /> {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="space-y-10 bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-sm">
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight">Fees & Finance</h2>
                <p className="text-[var(--color-accent)] font-bold uppercase tracking-widest text-[10px]">Payment Channels</p>
              </div>
              <div className="space-y-5">
                {data.payments.map((p: any, i: number) => (
                  <div key={i} className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-xl">
                    <div>
                      <p className="text-[10px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-1 italic">{p.method}</p>
                      <p className="text-base font-bold">{p.details}</p>
                    </div>
                    <ArrowUpRight size={18} className="text-slate-600 shrink-0" />
                  </div>
                ))}
                <div className="p-5 border-l-4 border-[var(--color-secondary)] bg-[var(--color-secondary)]/10 rounded-r-xl">
                  <p className="text-xs text-slate-400 leading-relaxed uppercase tracking-widest font-bold mb-2 italic">Policy Overview</p>
                  <p className="text-xs font-bold text-slate-300 italic">"{data.feePolicy}"</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUOTE & VALUES ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-7 p-10 bg-slate-50 rounded-3xl border border-slate-100">
              <Quote className="text-[var(--color-primary)] opacity-20" size={56} />
              <p className="text-xl font-light italic leading-relaxed text-slate-600">"{data.quote.text}"</p>
              <div className="flex items-center gap-4">
                <div className="w-10 h-1 bg-[var(--color-secondary)] rounded-full" />
                <p className="text-sm font-bold uppercase tracking-widest text-slate-900">{data.quote.author}</p>
              </div>
            </div>
            <div className="space-y-8">
              <div className="space-y-3">
                <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">Our Character</span>
                <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-primary)]">Core Values</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {data.coreValues.map((v: string, i: number) => (
                  <div key={i} className="p-5 border border-slate-100 rounded-2xl flex items-center gap-3 bg-white shadow-sm hover:border-[var(--color-secondary)] transition-all">
                    <div className="w-2 h-2 bg-[var(--color-primary)] rounded-full shrink-0" />
                    <span className="text-sm font-bold text-slate-700 uppercase tracking-wide">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" className="bg-slate-950 text-white pt-24 pb-12 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-16 mb-20">
            <div className="space-y-7">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[var(--color-primary)] rounded-xl flex items-center justify-center font-bold overflow-hidden">
                  {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="h-full w-full object-contain p-1" /> : <span className="text-white font-bold text-lg">{data.name.charAt(0)}</span>}
                </div>
                <h2 className="text-xl font-bold font-serif tracking-tight leading-tight">{data.name}</h2>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed italic">"{data.motto}"</p>
              <div className="flex flex-col gap-4">
                {data.contact.whatsapp && (
                  <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" className="bg-green-600/20 border border-green-500/30 text-green-400 px-5 py-2.5 rounded-lg font-bold text-xs tracking-widest hover:bg-green-600/30 transition-all flex items-center gap-2 w-fit">
                    <MessageCircle size={16} /> Chat on WhatsApp
                  </a>
                )}
                <div className="flex gap-3">
                  {data.contact.facebook && <a href={data.contact.facebook} aria-label="Facebook" className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-[var(--color-primary)] transition-all"><Facebook size={16} /></a>}
                  {data.contact.instagram && <a href={data.contact.instagram} aria-label="Instagram" className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-[var(--color-primary)] transition-all"><Instagram size={16} /></a>}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-10">
              <div className="space-y-5">
                <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--color-secondary)]">Contact Official</h4>
                <ul className="space-y-4">
                  {data.contact.phones.map((p: string, i: number) => <li key={i} className="flex items-center gap-3 text-sm text-slate-400"><Phone size={14} className="text-[var(--color-accent)] shrink-0" /> {p}</li>)}
                  <li className="flex items-center gap-3 text-sm text-slate-400"><Mail size={14} className="text-[var(--color-accent)] shrink-0" /> {data.contact.email}</li>
                  <li className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed"><MapPin size={14} className="text-[var(--color-accent)] shrink-0 mt-0.5" /> {data.contact.address}</li>
                </ul>
              </div>
              <div className="space-y-5">
                <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--color-secondary)]">Institutional Maps</h4>
                <div className="h-44 bg-white/5 rounded-2xl border border-white/10 overflow-hidden relative">
                  {data.contact.mapsLink ? (
                    <iframe title="School location map" src={data.contact.mapsLink} className="w-full h-full border-none grayscale pointer-events-none" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs italic">Map location not provided.</div>
                  )}
                  <a href={data.contact.mapsLink} aria-label="Open map" className="absolute bottom-2 right-2 bg-[var(--color-primary)] text-white p-2 rounded-lg"><ArrowUpRight size={14} /></a>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 flex justify-between items-center">
            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">&copy; {new Date().getFullYear()} {data.name}. All Rights Reserved.</p>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top" className="w-11 h-11 rounded-xl bg-[var(--color-primary)] flex items-center justify-center hover:opacity-80 transition-all shadow-xl">
              <ChevronDown className="text-white rotate-180" size={22} />
            </button>
          </div>
        </div>
      </footer>

      {/* ── EXPANDED DIRECTORY OVERLAY ── */}
      {expandedView && (
        <div className="fixed inset-0 z-[200] bg-white overflow-y-auto pb-24">
          <div className="bg-white border-b px-6 py-8 flex items-center justify-between sticky top-0 z-50 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => setExpandedView(null)} title="Close expanded view" aria-label="Close expanded view" className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-all">
                <ChevronLeft size={22} />
              </button>
              <div>
                <h2 className="text-xl font-bold font-serif text-slate-900">
                  {expandedView === 'staff' ? 'Academic Faculty Directory' : expandedView === 'gallery' ? 'Visual Archive' : expandedView === 'facilities' ? 'School Facilities Directory' : expandedView === 'anthems' ? 'Institutional Anthems' : 'Board of Management Directory'}
                </h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{data.name}</p>
              </div>
            </div>
            <button onClick={() => setExpandedView(null)} title="Close expanded view" aria-label="Close expanded view" className="text-slate-400 hover:text-slate-900 p-2"><X size={22} /></button>
          </div>

          <div className="max-w-7xl mx-auto px-6 py-14">
            {expandedView === 'gallery' ? (
              <>
                <div className="flex flex-wrap gap-2 justify-center mb-10">
                  {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                    <button key={cat} onClick={() => setActiveTab(cat)} className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all border ${activeTab === cat ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)]'}`}>
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filteredGallery.map((item: any, i: number) => (
                    <div key={i} onClick={() => setLightboxIndex(i)} className="cursor-pointer relative overflow-hidden rounded-2xl group shadow-sm aspect-square bg-slate-200">
                      {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                        <p className="text-[9px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-1">{item.categoryId || item.category}</p>
                        {item.name && <h4 className="text-white font-bold text-base leading-tight">{item.name}</h4>}
                        {item.description && <p className="text-slate-300 text-xs leading-relaxed line-clamp-3 mt-1">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : expandedView === 'facilities' ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {data.facilities.map((f: any, i: number) => (
                  <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 hover:border-[var(--color-secondary)] transition-all shadow-sm">
                    <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-slate-100">
                      {f.photoFile ? <img src={safeUrl(f.photoFile)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="Facility" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="text-slate-300" /></div>}
                    </div>
                    <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-widest mb-1 italic">{f.categoryId}</p>
                    <h4 className="font-bold font-serif text-slate-900 mb-1">{f.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{f.description}</p>
                  </div>
                ))}
              </div>
            ) : expandedView === 'anthems' ? (
              <div className="max-w-4xl mx-auto space-y-20">
                <div className="space-y-10">
                  <div className="text-center space-y-3">
                    <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">The Republic of Kenya</span>
                    <h3 className="text-3xl font-bold font-serif text-slate-900">National Anthem</h3>
                  </div>
                  <div className="aspect-video w-full rounded-3xl overflow-hidden shadow-xl bg-slate-100 relative">
                    <img src="https://images.unsplash.com/photo-1608925565154-2974eb36e6ba?q=80&w=2070" className="w-full h-full object-cover" alt="National Anthem backdrop" />
                    <div className="absolute inset-0 bg-slate-900/40" />
                  </div>
                  <div className="bg-slate-50 p-10 md:p-14 rounded-3xl border border-slate-100 text-center shadow-sm">
                    <p className="whitespace-pre-line text-lg text-slate-700 leading-loose italic font-medium">{data.nationalAnthem}</p>
                  </div>
                </div>
                <div className="space-y-10 pt-14 border-t border-slate-200">
                  <div className="text-center space-y-3">
                    <span className="text-[var(--color-secondary)] font-bold uppercase tracking-widest text-xs">{data.name}</span>
                    <h3 className="text-3xl font-bold font-serif text-slate-900">School Anthem</h3>
                  </div>
                  <div className="aspect-video w-full rounded-3xl overflow-hidden shadow-xl bg-slate-100 relative">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070" className="w-full h-full object-cover" alt="School Anthem backdrop" />
                    <div className="absolute inset-0 bg-slate-900/40" />
                  </div>
                  <div className="bg-slate-50 p-10 md:p-14 rounded-3xl border border-slate-100 text-center shadow-sm">
                    <p className="whitespace-pre-line text-lg text-slate-700 leading-loose italic font-medium">{data.schoolAnthem}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                {(expandedView === 'staff' ? data.staff : data.board).map((item: any, i: number) => (
                  <div key={i} className="flex flex-col items-center text-center space-y-4">
                    <div className="w-36 h-36 rounded-3xl overflow-hidden bg-slate-50 border shadow-sm flex items-center justify-center">
                      {(item.photoFile || item.photo) ? (
                        <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Profile"} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-200">
                          {expandedView === 'staff' ? <Users size={52} /> : <School size={52} />}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold font-serif text-slate-900 text-base">{item.title} {item.name}</h3>
                      <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-widest mt-1">{expandedView === 'staff' ? (item.bio || 'Faculty') : item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LIGHTBOX ── */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div className="fixed inset-0 bg-black flex items-center justify-center" style={{ zIndex: 99999 }}>
          <button onClick={() => setLightboxIndex(null)} className="absolute top-6 left-6 z-50 text-white/70 hover:text-white transition-all flex items-center gap-2 font-bold uppercase tracking-widest text-xs bg-black/40 px-5 py-2.5 rounded-full backdrop-blur-sm border border-white/10">
            <ChevronLeft size={14} /> Back to Gallery
          </button>
          <div className="absolute inset-0 w-full h-full">
            {(filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo ? (
              <img src={safeUrl((filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo)} alt={(filteredGallery[lightboxIndex] as any).name || "Gallery"} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full bg-slate-900 flex items-center justify-center text-white/20"><ImageIcon size={80} /></div>
            )}
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 bg-black/70 backdrop-blur-xl border border-white/10 px-7 py-5 rounded-2xl max-w-2xl w-[90%] text-center shadow-2xl">
            <p className="text-[9px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-1">{(filteredGallery[lightboxIndex] as any).categoryId || (filteredGallery[lightboxIndex] as any).category}</p>
            <h3 className="text-white font-bold text-xl tracking-tight leading-tight mb-1">{(filteredGallery[lightboxIndex] as any).name || "Untitled"}</h3>
            <p className="text-slate-300 text-xs leading-relaxed hidden md:block">{(filteredGallery[lightboxIndex] as any).description || ""}</p>
          </div>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-between px-6 md:px-12 z-20">
            {lightboxIndex > 0 ? (
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }} className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 p-5 rounded-full backdrop-blur-md border border-white/10">
                Prev
              </button>
            ) : <div />}
            {lightboxIndex < filteredGallery.length - 1 ? (
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }} className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 p-5 rounded-full backdrop-blur-md border border-white/10">
                Next
              </button>
            ) : <div />}
          </div>
        </div>
      )}
    </div>
  );
};

export default Design1_ModernElite;
